import { useMeasurementUnit } from 'hooks/useMeasurementUnit';
import './SkuTesterWizard.less';

import { Affix, Divider, List, PageHeader, Progress, Spin, Steps } from 'antd';
import Text from 'antd/lib/typography/Text';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  ArrowLeftIcon,
  DoneCircleIcon,
  UncheckedCircleIcon,
} from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxMoney } from 'components/RevxMoney';
import { Headline } from 'components/RevxTypography';
import { MultiTypeEnum, SKU } from 'domains/skuDomain';
import { isEmpty } from 'lodash';
import { sortBy } from 'lodash-es';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ADMIN_DETAIL_AFFIX_TOP } from 'utils/constants';
import { SkuTesterStepFinishedSize } from './SkuTesterStepFinishedSize';
import { SkuTesterStepOrientation } from './SkuTesterStepOrientation';
import { SkuTesterStepPageCount } from './SkuTesterStepPageCount';
import { SkuTesterStepPaper } from './SkuTesterStepPaper';
import { SkuTesterStepPrinting } from './SkuTesterStepPrinting';
import { SkuTesterStepQuantity } from './SkuTesterStepQuantity';
import { SkuTesterStepServices } from './SkuTesterStepServices';
import { SkuTesterStepShipping } from './SkuTesterStepShipping';

const { Step } = Steps;

type JobConfigStep = {
  title: string;
  description: string;
  content: ReactNode;
};

type JobCharge = { name: string; price: number };

const SkuTesterWizard = () => {
  const { t } = useTranslation();
  const { displaySize } = useMeasurementUnit();
  const dispatch = useAppDispatch();

  const sku = useAppSelector((state) => state.sku.skuTesterState.curItem);
  useEffect(() => {
    sku && dispatch.sku.getJobSpec(sku.id);
  }, [dispatch.sku, sku]);

  const jobSpec = useAppSelector((state) => state.sku.skuTesterState.jobSpec);
  const loading = useAppSelector(
    (state) => state.loading.effects.sku.getJobSpec
  );

  const onCancel = () => {
    dispatch.sku.resetSkuTester();
  };

  const steps: JobConfigStep[] = useMemo(() => {
    if (!sku || !jobSpec) {
      return [];
    }
    const noneSelected = t('platform.sku.tester.wizard.noneSelected');
    const steps: JobConfigStep[] = [];
    const stepQty: JobConfigStep = {
      title: t('platform.sku.tester.stepQuantity.navTitle'),
      description: jobSpec.quantity.toString() || noneSelected,
      content: <SkuTesterStepQuantity spec={jobSpec} />,
    };
    steps.push(stepQty);

    const stepOrientation: JobConfigStep = {
      title: t('platform.sku.tester.stepOrientation.navTitle'),
      description: jobSpec.orientation || noneSelected,
      content: (
        <SkuTesterStepOrientation
          sku={
            sku.multiType === MultiTypeEnum.WRAPPER ? sku : sku.parts[0].part
          }
          spec={jobSpec}
        />
      ),
    };
    steps.push(stepOrientation);

    const stepSize: JobConfigStep = {
      title: t('platform.sku.tester.stepFinishedSize.navTitle'),
      description: displaySize(jobSpec.finishedSizeIn, { metric: jobSpec.finishedSizeMetric }) || noneSelected,
      content: (
        <SkuTesterStepFinishedSize
          sku={
            sku.multiType === MultiTypeEnum.WRAPPER ? sku : sku.parts[0].part
          }
          spec={jobSpec}
        />
      ),
    };
    steps.push(stepSize);

    const skuPartMap: Map<number, SKU> = toPartMap(sku);

    sortBy(
      jobSpec.parts,
      (s) => sku.parts.find((p) => p.part.id === s.skuId)?.sequence || 0
    ).forEach((partSpec) => {
      const partSku = skuPartMap.get(partSpec.skuId);
      if (partSku) {
        if (partSku.multiPage) {
          steps.push({
            title:
              t('platform.sku.tester.stepPageCount.navTitle') +
              ' - ' +
              partSku.name,
            description: partSpec.pageCount?.toString() || noneSelected,
            content: <SkuTesterStepPageCount spec={partSpec} />,
          });
        }
        steps.push({
          title:
            t('platform.sku.tester.stepPaper.navTitle') + ' - ' + partSku.name,
          description: partSpec.paperBrand || noneSelected,
          content: <SkuTesterStepPaper spec={partSpec} />,
        });
        // TODO: how to generate printing desc
        steps.push({
          title:
            t('platform.sku.tester.stepPrinting.navTitle') +
            ' - ' +
            partSku.name,
          description: partSpec.inkFront || noneSelected,
          content: <SkuTesterStepPrinting spec={partSpec} />,
        });
        // TODO: how to dertermine a part needs service step?
        // TODO: how to display service 'selected options'?
        if (
          !isEmpty(partSku.requiredSvcIds) ||
          !isEmpty(partSku.requiredSvcTypeIds) ||
          !isEmpty(partSku.optionalSvcTypeIds)
        ) {
          steps.push({
            title:
              t('platform.sku.tester.stepServices.navTitle') +
              ' - ' +
              partSku.name,
            description:
              partSpec.fulfillment.jobSvcFulfillments.length.toString(),
            content: <SkuTesterStepServices spec={partSpec} />,
          });
        }
      }
    });

    const stepSvc: JobConfigStep = {
      title: t('platform.sku.tester.stepServices.navTitle'),
      description: isEmpty(jobSpec.fulfillment.jobSvcFulfillments)
        ? noneSelected
        : jobSpec.fulfillment.jobSvcFulfillments[0].typeName,
      content: <SkuTesterStepServices spec={jobSpec} />,
    };
    steps.push(stepSvc);

    const stepShipping: JobConfigStep = {
      title: t('platform.sku.tester.stepShipping.navTitle'),
      description: isEmpty(jobSpec.shippings)
        ? noneSelected
        : jobSpec.shippings[0].expectedDeliveryDate || '',
      content: <SkuTesterStepShipping spec={jobSpec} />,
    };
    steps.push(stepShipping);

    return steps;
  }, [sku, jobSpec, t, displaySize]);

  const totalStep = steps.length - 1;
  const [currentStep, setCurrentStep] = useState(0);

  const charges: JobCharge[] = useMemo(() => {
    return [
      { name: 'Paper', price: jobSpec?.fulfillment?.paperCharge || 0 },
      {
        name: 'Printing',
        price: jobSpec?.fulfillment?.printingCharge || 0,
      },
      {
        name: 'Shipping',
        price: jobSpec?.fulfillment?.freightCharge || 0,
      },
      { name: 'Total', price: jobSpec?.fulfillment?.totalCharge || 0 },
    ];
  }, [jobSpec]);

  const actionBar = (
    <div>
      <Affix offsetTop={ADMIN_DETAIL_AFFIX_TOP}>
        <PageHeader
          className="platform-item-form-header"
          onBack={() => {
            onCancel();
          }}
          backIcon={<ArrowLeftIcon />}
          title={
            <Headline>
              {t('platform.sku.tester.wizard.headline', { skuName: sku?.name })}
            </Headline>
          }
          extra={[
            <>
              <RevxButton onClick={onCancel} brandGreen key="cancel">
                {t('common.cancel')}
              </RevxButton>
            </>,
          ]}
        ></PageHeader>
      </Affix>
    </div>
  );

  const percent = useMemo(() => {
    if (currentStep >= totalStep) {
      return 100;
    }
    return Math.ceil((currentStep * 100) / totalStep);
  }, [currentStep, totalStep]);

  return (
    <Spin spinning={loading}>
      <div className="sku-tester-wizard-wrapper">
        {actionBar}
        <div className="sku-tester-wizard">
          <RevxFlexLayout>
            <div className="sku-tester-wizard-left">
              <div className="sku-tester-wizard-progress-panel">
                <div className="sku-tester-wizard-title">
                  {t('platform.sku.tester.wizard.title')}
                </div>

                <div style={{ marginRight: 0 }}>
                  <Progress
                    percent={percent}
                    status={currentStep < totalStep ? 'exception' : 'active'}
                    showInfo={false}
                  />
                </div>
                <Text>
                  {percent}% {t('platform.sku.wizard.complete')}
                </Text>
                <RevxButton
                  brandGreen
                  className="step-btn"
                  onClick={() => {
                    if (currentStep + 1 > totalStep) {
                      setCurrentStep(0);
                      onCancel();
                      return;
                    }
                    setCurrentStep(currentStep + 1);
                  }}
                >
                  {t('platform.sku.wizard.nextStep')}
                </RevxButton>
              </div>
              <Divider type="horizontal" />

              <Steps
                direction="vertical"
                current={currentStep}
                progressDot={(iconDot, { index, status }) => {
                  return status === 'process' ? (
                    <DoneCircleIcon />
                  ) : index < currentStep ? (
                    <DoneCircleIcon />
                  ) : (
                    <UncheckedCircleIcon />
                  );
                }}
                onChange={(step) => {
                  if (step < currentStep) {
                    setCurrentStep(step);
                  }

                  if (step >= totalStep) {
                    onCancel();
                  }
                }}
              >
                {steps.map((step) => (
                  <Step title={step.title} description={step.description} />
                ))}
              </Steps>
            </div>
            <div className="sku-tester-wizard-center">
              <div className="sku-tester-wizard-step">
                {steps?.[currentStep]?.content}
              </div>
            </div>
            <div className="sku-tester-wizard-right">
              <div className="sku-tester-wizard-price-panel">
                <List
                  header={<div>Organization</div>}
                  size="small"
                  dataSource={charges}
                  renderItem={(item) => (
                    <List.Item actions={[<a key="detail">detail</a>]}>
                      {item.name}: <RevxMoney value={item.price} />
                    </List.Item>
                  )}
                ></List>
              </div>
            </div>
          </RevxFlexLayout>
        </div>
      </div>
    </Spin>
  );
};

export default SkuTesterWizard;

function toPartMap(sku: SKU): Map<number, SKU> {
  const skuMap = new Map<number, SKU>();
  skuMap.set(sku.id, sku);
  if (sku.multiType === MultiTypeEnum.WRAPPER && !isEmpty(sku.parts)) {
    sku.parts.forEach((part) => skuMap.set(part.part.id, part.part));
  }
  return skuMap;
}
